import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TippyModule } from '@ngneat/helipopper';
import { TranslateModule } from '@ngx-translate/core';

import { BreadcrumbItem } from '@core/types';
import { isMobileView } from '@core/utils';
import { IconComponent } from '@shared/components/ui/icon/icon.component';

@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  imports: [CommonModule, IconComponent, RouterLink, TippyModule, TranslateModule],
})

/**
 * Page header component.
 * Displays page header title and breadcrumbs.
 * Content project is used to display any content on the right side of the page header.
 * Breadcrumbs are displayed below the page header title.
 *
 * @property {string} headerTitle - Page header title.
 * @property {string} helpText - Help text to display in tooltip. If provided, a question-circle icon will be displayed next to the title.
 * @property {boolean} helpTextIsHtml - Whether the help text should be rendered as HTML. Default is false.
 * @property {BreadcrumbItem[]} breadcrumbs - Breadcrumb items.
 * @property {Object} headerTitleStyles - CSS style object. Pass style object as is. Applied to the page header title.
 * @property {Object} breadcrumbsStyles - CSS style object. Pass style object as is. Applied to the breadcrumbs container.
 * @property {string} cssClass - CSS class name. Pass class name as string. Applied to the page header parent div.
 * @property {boolean} titleOnly - Show title only
 *
 */
export class PageHeaderComponent {
  /**
   * Page header title
   */
  @Input() headerTitle: string;
  /**
   * Breadcrumb items
   * Show breadcrumbs below the page header title.
   * @Type {BreadcrumbItem[]}
   */
  @Input() breadcrumbs: BreadcrumbItem[] = [];
  /**
   * CSS style object.
   * Pass style object as is.
   *
   * Applied to the page header title.
   */
  @Input() headerTitleStyles: Record<string, string> = {};
  /**
   * CSS style object.
   * Pass style object as is.
   *
   * Applied to the breadcrumbs container.
   */
  @Input() breadcrumbsStyles: Record<string, string> = {};
  /**
   * CSS class name.
   * Pass class name as string.
   * Applied to the page header parent div.
   */
  @Input() cssClass: string;
  /**
   * Show back button.
   * Default value is false.
   * If true, show back button on the left side of the page header over the Title.
   */
  @Input() showBackButton = false;
  /**
   * Show back button on mobile only.
   * Default value is false.
   * If true, show back button on mobile view only.
   */
  @Input() showBackButtonOnMobileOnly = false;
  /**
   * Back link.
   * If showBackButton is true, provide back link to the page where the user will be redirected on clicking the back button.
   * By default, back link will take the user to main page.
   */
  @Input() backLink: string;
  /**
   * Show title only.
   */
  @Input() titleOnly = false;
  /**
   * Prefix icon name.
   * If provided, show prefix icon on the left side of the page header title.
   * Icon name should be provided as string.
   */
  @Input() prefixIconName: string;
  /**
   * Prefix icon size.
   * If provided, set the size of the prefix icon.
   * @Default '26px'
   */
  @Input() prefixIconSize = '26px';
  /**
   * Prefix icon color.
   * If provided, set the color of the prefix icon.
   */
  @Input() prefixIconColor: string;
  /**
   * Help text to display in tooltip.
   * If provided, a question-circle icon will be displayed next to the title.
   */
  @Input() helpText?: string;
  /**
   * Whether the help text should be rendered as HTML.
   * Default is false.
   */
  @Input() helpTextIsHtml = false;
  protected readonly isMobileView = isMobileView();
}
